import { useState } from "react";
import { getTraitIconPath, filterTraits } from "@/lib/utils/trait-icons";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { getTraitDisplayName, getTraitDescription } from "@/lib/utils/trait-names";

interface TraitIconProps {
  speciality: string;
  size?: "sm" | "md" | "lg";
  showText?: boolean;
  showTooltip?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-6 h-6",
};

export function TraitIcon({ 
  speciality, 
  size = "md", 
  showText = false,
  showTooltip = true,
  className = "" 
}: TraitIconProps) {
  const [imageError, setImageError] = useState(false);
  const isTrait = speciality.startsWith("_");
  const iconPath = isTrait ? getTraitIconPath(speciality) : null;
  const hasError = imageError || !iconPath;

  if (!isTrait) {
    // Non-trait speciality - show as badge
    return (
      <Badge variant="secondary" className={className}>
        {speciality}
      </Badge>
    );
  }

  // Trait - show icon with optional text
  const displayName = getTraitDisplayName(speciality);
  const description = getTraitDescription(speciality);
  
  const iconContent = (
    <div
      className={`${sizeClasses[size]} rounded bg-pink-500/20 border border-pink-500/40 flex items-center justify-center overflow-hidden ${className}`}
    >
      {iconPath && !hasError ? (
        <img
          src={iconPath}
          alt={displayName}
          className="w-full h-full object-contain"
          style={{
            filter: 'var(--trait-icon-filter)'
          }}
          onError={() => setImageError(true)}
        />
      ) : (
        <span className={`text-pink-500 font-bold ${size === "sm" ? "text-[8px]" : size === "md" ? "text-[10px]" : "text-xs"}`}>
          {speciality.replace("_", "").charAt(0).toUpperCase()}
        </span>
      )}
      {showText && (
        <span className="ml-1 text-xs text-muted-foreground">
          {speciality.replace("_", "")}
        </span>
      )}
    </div>
  );
  
  // If tooltip is disabled, just return the icon content
  if (!showTooltip) {
    return iconContent;
  }
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {iconContent}
      </TooltipTrigger>
      <TooltipContent className="max-w-xs z-[100]">
        <div className="space-y-1">
          <div className="font-semibold">{displayName}</div>
          <div className="text-sm">{description}</div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

